import React from 'react'
import {
  Button,
  SearchWithFilter,
  SortFilter,
  ToggleSwitch,
  type FilterSelection,
  type SortFilterSection,
} from '@enamelai/design-system'
import './FilesAndLettersHeader.css'

export type SortByOption = 'name' | 'dateModified' | 'itemType'
export type SortDirectionOption = 'none' | 'aToZ' | 'zToA'
export type FoldersOption = 'onTop' | 'mixedWithFiles'

export type SortConfig = {
  sortBy: SortByOption
  sortDirection: SortDirectionOption
  folders: FoldersOption
}

export type FilesAndLettersHeaderProps = {
  patientName: string
  searchTerm: string
  onSearchChange: (value: string) => void
  searchFilters: FilterSelection
  onFilterChange: (filters: FilterSelection) => void
  onResetFilters: () => void
  typeFilterOptions?: string[]
  onCreateFolder: () => void
  onUpload: () => void
  sortConfig: SortConfig
  onSortChange: (config: SortConfig) => void
  isPreviewOpen?: boolean
  onPreviewToggle?: (value: boolean) => void
  selectionMode?: boolean
  selectedCount?: number
  onOpen?: () => void
  onCancel?: () => void
  /** When true, disables preview switch, search, sort, and action buttons */
  actionsDisabled?: boolean
  /** When true, disables only the Upload button (empty folder already has Upload). */
  uploadDisabled?: boolean
}

const SORT_SECTIONS: SortFilterSection[] = [
  {
    id: 'sortBy',
    title: 'Sort By',
    options: [
      { id: 'name', label: 'Name' },
      { id: 'dateModified', label: 'Date Modified' },
      { id: 'itemType', label: 'Item Type' },
    ],
  },
  {
    id: 'sortDirection',
    title: 'Sort Direction',
    options: [
      { id: 'none', label: 'None' },
      { id: 'aToZ', label: 'A to Z' },
      { id: 'zToA', label: 'Z - A' },
    ],
  },
  {
    id: 'folders',
    title: 'Folders',
    options: [
      { id: 'onTop', label: 'On top' },
      { id: 'mixedWithFiles', label: 'Mixed with files' },
    ],
  },
]

function FilesAndLettersHeader({
  patientName,
  searchTerm,
  onSearchChange,
  searchFilters,
  onFilterChange,
  onResetFilters,
  typeFilterOptions = [],
  onCreateFolder,
  onUpload,
  sortConfig,
  onSortChange,
  isPreviewOpen = false,
  onPreviewToggle,
  selectionMode = false,
  selectedCount = 0,
  onOpen,
  onCancel,
  actionsDisabled = false,
  uploadDisabled = false,
}: FilesAndLettersHeaderProps): React.ReactElement {
  const handleSortChange = (sectionId: string, optionId: string): void => {
    onSortChange({ ...sortConfig, [sectionId]: optionId })
  }

  return (
    <div className="files-and-letters-header">
      <div className="files-and-letters-header-left">
        <span className="files-and-letters-patient-name">{patientName}</span>
      </div>
      <div className="files-and-letters-header-right">
        <label
          className={`fal-preview-switch${actionsDisabled ? ' fal-preview-switch--disabled' : ''}`}
          htmlFor="preview-panel-switch"
        >
          <ToggleSwitch
            id="preview-panel-switch"
            aria-label="Preview Panel"
            checked={isPreviewOpen}
            onChange={(checked) => onPreviewToggle?.(checked)}
            disabled={actionsDisabled}
          />
          <span className="fal-preview-switch-label">Preview Panel</span>
        </label>

        <SearchWithFilter
          placeholder="Search files"
          value={searchTerm}
          onChange={onSearchChange}
          onClear={() => onSearchChange('')}
          filters={{ type: typeFilterOptions }}
          filterLabels={{ type: 'Type' }}
          selectedFilters={searchFilters}
          onFilterChange={onFilterChange}
          onReset={onResetFilters}
          showResetButton
          disabled={actionsDisabled}
          className="files-and-letters-search"
        />

        <SortFilter
          mode="sort"
          ariaLabel="Sort files"
          sections={SORT_SECTIONS}
          values={sortConfig}
          onChange={handleSortChange}
          disabled={actionsDisabled}
        />

        <span className="fal-header-divider" aria-hidden />

        {selectionMode ? (
          <>
            {onCancel && (
              <Button
                variant="secondary"
                size="sm"
                onClick={onCancel}
                disabled={actionsDisabled}
              >
                Cancel
              </Button>
            )}
            {onOpen && (
              <Button
                variant="primary"
                size="sm"
                onClick={onOpen}
                disabled={actionsDisabled || selectedCount === 0}
              >
                Open{selectedCount > 0 ? ` (${selectedCount})` : ''}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={onCreateFolder}
              disabled={actionsDisabled}
            >
              Create folder
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={onUpload}
              disabled={actionsDisabled || uploadDisabled}
            >
              Upload
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

export default FilesAndLettersHeader
